// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useModal,
  useNavigationDOM,
  usePopover,
  useResponsive,
  useToast,
  ModalType,
} from '@supermove/hooks';
import {Inventory, InventoryModel, ProjectModel} from '@supermove/models';
import {NavigationType} from '@supermove/navigation/src/NavigationTypes';
import {colors} from '@supermove/styles';
import {Currency, pluralize} from '@supermove/utils';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import FieldValue from '@shared/design/components/Field/FieldValue';
import IconButton from '@shared/design/components/IconButton';
import CautionModal from '@shared/design/components/Modal/SmallModal/CautionModal';
import Toast from '@shared/design/components/Toast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import UpdateInventoryKindVirtualWalkthroughModal from '@shared/modules/Inventory/components/UpdateInventoryKindVirtualWalkthroughModal';
import InventoryForm from '@shared/modules/Inventory/forms/InventoryForm';
import InventorySurveyMethodForm from '@shared/modules/Inventory/forms/InventorySurveyMethodForm';
import useSyncInventoryBillMutation from '@shared/modules/Inventory/hooks/useSyncInventoryBillMutation';
import useUpdateInventorySurveyMethodMutation from '@shared/modules/Inventory/hooks/useUpdateInventorySurveyMethodMutation';
import ConfirmSyncInventoryBillModal from '@shared/modules/Project/components/widgets/components/ConfirmSyncInventoryBillModal';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';
import ProjectBlockKind, {
  ProjectBlockKindType,
} from '@shared/modules/Project/enums/ProjectBlockKind';

const BLOCK_TITLE = 'Survey';

const Container = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const IndexedContainer = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const getGenerateBillLabel = ({hasSyncedBill}: {hasSyncedBill: boolean}) => {
  return hasSyncedBill ? 'Regenerate Bill' : 'Generate Bill';
};

const handleGenerateBill = ({
  hasSyncedBill,
  confirmSyncInventoryBillModal,
  syncInventoryBillHandleSubmit,
}: {
  hasSyncedBill: boolean;
  confirmSyncInventoryBillModal: ModalType;
  syncInventoryBillHandleSubmit: () => void;
}) => {
  if (hasSyncedBill) {
    confirmSyncInventoryBillModal.handleOpen();
  } else {
    syncInventoryBillHandleSubmit();
  }
};

const getVirtualWalkthroughLabel = ({
  isVirtualWalkthroughStarted,
  isEnabledSurveysVirtualWalkthroughSupport,
}: {
  isVirtualWalkthroughStarted: boolean;
  isEnabledSurveysVirtualWalkthroughSupport: boolean;
}) => {
  const viewText = isEnabledSurveysVirtualWalkthroughSupport
    ? 'Survey Items'
    : 'View Virtual Walkthrough';

  return isVirtualWalkthroughStarted ? viewText : 'Send Walkthrough Link';
};

const handleVirtualWalkthrough = ({
  inventory,
  project,
  navigator,
  isVirtualWalkthroughStarted,
  virtualWalkthroughModal,
  isEnabledSurveysVirtualWalkthroughSupport,
}: {
  inventory: InventoryModel;
  project: ProjectModel;
  navigator: NavigationType;
  isVirtualWalkthroughStarted: boolean;
  virtualWalkthroughModal: ModalType;
  isEnabledSurveysVirtualWalkthroughSupport: boolean;
}) => {
  if (isVirtualWalkthroughStarted) {
    isEnabledSurveysVirtualWalkthroughSupport
      ? navigator.push(`/projects/${project.uuid}/inventory/${project.inventory.uuid}?back=1`)
      : navigator.push(`/inventories/${inventory.uuid}/edit`);
  } else {
    virtualWalkthroughModal.handleOpen();
  }
};

const SURVEY_ITEMS_LABEL = 'Survey Items';

const handleSurveyItems = ({
  project,
  navigator,
}: {
  project: ProjectModel;
  navigator: NavigationType;
}) => {
  navigator.push(`/projects/${project.uuid}/inventory/${project.inventory.uuid}?back=1`);
};

const SkeletonComponent = () => {
  return (
    <React.Fragment>
      <BlockHeaderLoader
        title={BLOCK_TITLE}
        ActionsComponent={() => <IconButton isDisabled source={Icon.EllipsisV} />}
      />
      <Row>
        <FieldValue.SkeletonLoader label={'Est. Volume'} width={144} style={{flex: 1}} />
        <FieldValue.SkeletonLoader label={'Est. Weight'} width={144} style={{flex: 1}} />
      </Row>
      <Space height={16} />
      <Row>
        <FieldValue.SkeletonLoader label={'Est. Price'} width={144} style={{flex: 1}} />
        <FieldValue.SkeletonLoader label={'Rooms'} width={144} style={{flex: 1}} />
      </Row>
      <Space height={16} />
      <Row>
        <FieldValue.SkeletonLoader label={'Cartons'} width={144} style={{flex: 1}} />
        <FieldValue.SkeletonLoader label={'Non-Cartons'} width={144} style={{flex: 1}} />
      </Row>
      <Space height={16} />
      <Row>
        <FieldValue.SkeletonLoader label={'Pack Time'} width={144} style={{flex: 1}} />
        <FieldValue.SkeletonLoader label={'Unpack Time'} width={144} style={{flex: 1}} />
      </Row>
      <Space height={16} />
      <Row>
        <FieldValue.SkeletonLoader label={'Notes'} isFullWidth />
      </Row>
    </React.Fragment>
  );
};

const SurveyKindDropdown = ({project, refetch}: {project: ProjectModel; refetch: () => void}) => {
  const inventorySurveyMethodForm = InventorySurveyMethodForm.edit(project.inventory);
  const {form, handleSubmit} = useUpdateInventorySurveyMethodMutation({
    inventorySurveyMethodForm,
    onSuccess: refetch,
    onError: (errors) => console.log({errors}),
  });

  const methodLabel = project.inventory.surveyMethod?.name || 'Survey Method';
  const popover = usePopover({name: 'Survey Method Popover'});
  const actions = project.organization.surveyMethods.map((surveyMethod) => ({
    text: surveyMethod.name,
    onPress: () => {
      form.setFieldValue('inventorySurveyMethodForm.surveyMethodId', surveyMethod.id);
      setTimeout(handleSubmit, 0);
      if (popover.isOpen) {
        popover.handleClose();
      }
    },
  }));

  return (
    <DropdownButton
      text={methodLabel}
      isSmall
      actions={actions}
      ButtonComponent={SecondaryButton}
      buttonStyle={{backgroundColor: colors.blue.accent, borderColor: colors.blue.accent}}
      menuWidth={200}
      sheetLabel={'Change Survey Method'}
      isPortaled
      value={methodLabel}
    />
  );
};

const SurveyActions = ({
  project,
  refetch,
  handleUpdateBlock,
}: {
  project: ProjectModel;
  refetch: () => void;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
}) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();

  const {inventory, organization} = project;
  const {isEnabledSurveysVirtualWalkthroughSupport} = organization.features;
  const {hasSyncedBill, isVirtualWalkthroughStarted} = inventory;

  const isVirtualWalkthrough = Inventory.getIsVirtualWalkthrough(inventory);
  const isNoSurvey = Inventory.getIsNoSurvey(inventory);

  const surveyActionsPopover = usePopover({name: 'Survey Actions Popover'});
  const confirmSyncInventoryBillModal = useModal({name: 'Confirm Sync Inventory Bill Modal'});
  const confirmSurveyItemsModal = useModal({
    name: 'Confirm Survey Items Modal',
    enableTracking: false,
  });
  const virtualWalkthroughModal = useModal({name: 'Virtual Walkthrough Modal'});
  const virtualWalkthroughLinkSentSuccessToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Virtual walkthrough link sent.',
  });
  const selectSurveyMethodToast = useToast({
    ToastComponent: Toast,
    message: 'Select a survey method to see actions.',
  });
  const billGeneratedToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Bill generated!',
    actionText: 'View',
    handleAction: () => handleUpdateBlock(ProjectBlockKind.BILLING),
  });

  const inventoryForm = InventoryForm.edit(inventory);
  const {submitting: syncInventoryBillSubmitting, handleSubmit: syncInventoryBillHandleSubmit} =
    useSyncInventoryBillMutation({
      inventoryForm,
      onSuccess: () => {
        confirmSyncInventoryBillModal.handleClose();
        surveyActionsPopover.handleClose();
        billGeneratedToast.handleToast();
        refetch();
      },
      onError: (errors) => console.log({errors}),
      refetchQueries: ['ProjectBillingBlock', 'ProjectInvoicesBlock'],
    });

  return (
    <React.Fragment>
      <ActionMenuPopover
        popover={surveyActionsPopover}
        sheetLabel={'Actions'}
        width={200}
        actions={[
          // 1. "Send Walkthrough Link" action - disabled when conditions not met
          ...(isVirtualWalkthrough
            ? [
                {
                  text: 'Send Walkthrough Link',
                  onPress: () =>
                    handleVirtualWalkthrough({
                      inventory,
                      project,
                      navigator,
                      isVirtualWalkthroughStarted,
                      virtualWalkthroughModal,
                      isEnabledSurveysVirtualWalkthroughSupport,
                    }),
                  isDisabled: isVirtualWalkthroughStarted,
                },
              ]
            : []),
          // 2. (Re)Generate Bill
          {
            text: getGenerateBillLabel({hasSyncedBill}),
            onPress: () =>
              handleGenerateBill({
                hasSyncedBill,
                confirmSyncInventoryBillModal,
                syncInventoryBillHandleSubmit,
              }),
            isLoadedAction: !hasSyncedBill,
            isLoading: syncInventoryBillSubmitting,
          },
          // 3. Always-visible "View Virtual Walkthrough" or "Survey Items" action
          {
            text: isVirtualWalkthrough
              ? getVirtualWalkthroughLabel({
                  isVirtualWalkthroughStarted: true,
                  isEnabledSurveysVirtualWalkthroughSupport,
                })
              : SURVEY_ITEMS_LABEL,
            onPress: () =>
              isVirtualWalkthrough
                ? handleVirtualWalkthrough({
                    inventory,
                    project,
                    navigator,
                    isVirtualWalkthroughStarted: true,
                    virtualWalkthroughModal,
                    isEnabledSurveysVirtualWalkthroughSupport,
                  })
                : responsive.desktop
                  ? handleSurveyItems({project, navigator})
                  : confirmSurveyItemsModal.handleOpen(),
          },
        ]}
      >
        <IconButton
          source={Icon.EllipsisV}
          onPress={
            isNoSurvey ? selectSurveyMethodToast.handleToast : surveyActionsPopover.handleToggle
          }
        />
      </ActionMenuPopover>
      <ConfirmSyncInventoryBillModal
        key={confirmSyncInventoryBillModal.key}
        isOpen={confirmSyncInventoryBillModal.isOpen}
        handleClose={confirmSyncInventoryBillModal.handleClose}
        handleSubmit={syncInventoryBillHandleSubmit}
        submitting={syncInventoryBillSubmitting}
      />
      <UpdateInventoryKindVirtualWalkthroughModal
        key={virtualWalkthroughModal.key}
        isOpen={virtualWalkthroughModal.isOpen}
        inventoryUuid={inventory.uuid}
        handleClose={virtualWalkthroughModal.handleClose}
        onSuccess={() => {
          refetch();
          virtualWalkthroughLinkSentSuccessToast.handleToast();
        }}
      />
      <CautionModal
        key={confirmSurveyItemsModal.key}
        title={'Survey Items?'}
        message={
          'The survey items page has not been updated for mobile devices. Would you like to continue?'
        }
        isOpen={confirmSurveyItemsModal.isOpen}
        handleSecondaryAction={confirmSurveyItemsModal.handleClose}
        secondaryActionText={'Cancel'}
        handlePrimaryAction={() => handleSurveyItems({project, navigator})}
        primaryActionText={'Continue'}
      />
    </React.Fragment>
  );
};

const SurveyFields = ({project, isWidget}: {project: ProjectModel; isWidget?: boolean}) => {
  const {inventory} = project;
  const {collection, notes, totalRooms} = inventory;
  const {
    totalPrice,
    totalTakeCartonItems,
    totalTakeNonCartonItems,
    totalWeight,
    totalVolume,
    totalPackTime,
    totalUnpackTime,
  } = collection;

  const responsive = useResponsive();
  const desktopColumnCount = 2;
  const containerStyle = {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: `calc(100% * (1 / ${responsive.desktop && !isWidget ? desktopColumnCount : 2}))`,
  };

  return (
    <React.Fragment>
      <Row style={{flexWrap: 'wrap'}}>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Est. Volume'}
            value={`${_.round(totalVolume, 2)} cu ft`}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Est. Weight'}
            value={`${_.round(totalWeight, 2)} lbs`}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Est. Price'}
            value={Currency.display(totalPrice)}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Rooms'}
            value={pluralize('room', totalRooms, true)}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Carton Items'}
            value={pluralize('item', totalTakeCartonItems, true)}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Non-Carton Items'}
            value={pluralize('item', totalTakeNonCartonItems, true)}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Pack Time'}
            value={`${_.round(totalPackTime, 2)} min`}
            isResponsive
          />
          <Space height={16} />
        </Container>
        <Container style={containerStyle}>
          <FieldValue
            style={{flex: 1}}
            label={'Unpack Time'}
            value={`${_.round(totalUnpackTime, 2)} min`}
            isResponsive
          />
          <Space height={16} />
        </Container>
      </Row>
      <Row>
        <FieldValue style={{flex: 1}} label={'Notes'} value={notes} empty={'None'} isResponsive />
      </Row>
    </React.Fragment>
  );
};

const ProjectDetailsSurveyBlockContent = ({
  project,
  refetch,
  handleUpdateBlock,
}: {
  project: ProjectModel;
  refetch: () => void;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={BLOCK_TITLE}
        ActionsComponent={SurveyActions}
        actionsComponentProps={{project, refetch, handleUpdateBlock}}
      />
      <IndexedContainer index={0}>
        <SurveyKindDropdown project={project} refetch={refetch} />
      </IndexedContainer>
      <IndexedContainer index={1}>
        <Space height={16} />
        <SurveyFields project={project} />
      </IndexedContainer>
    </React.Fragment>
  );
};

const ProjectDetailsSurveyBlock = ({
  projectUuid,
  index,
  handleUpdateBlock,
}: {
  projectUuid: string;
  index: number;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      key={projectUuid}
      query={ProjectDetailsSurveyBlock.query}
      queryVariables={{projectUuid}}
      index={index}
      SkeletonComponent={SkeletonComponent}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return (
          <ProjectDetailsSurveyBlockContent
            project={data.project}
            refetch={refetch}
            handleUpdateBlock={handleUpdateBlock}
          />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsSurveyBlock.query = gql`
  ${Inventory.getIsNoSurvey.fragment}
  ${Inventory.getIsVirtualWalkthrough.fragment}
  ${InventoryForm.edit.fragment}
  ${InventorySurveyMethodForm.edit.fragment}
  query ProjectDetailsSurveyBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      inventory {
        id
        uuid
        notes
        totalRooms
        isVirtualWalkthroughStarted
        hasSyncedBill
        collection {
          id
          totalPrice
          totalVolume
          totalWeight
          totalTakeCartonItems
          totalTakeNonCartonItems
          totalPackTime
          totalUnpackTime
        }
        surveyMethod {
          id
          name
        }
        ...Inventory_getIsNoSurvey
        ...Inventory_getIsVirtualWalkthrough
        ...InventoryForm_edit
        ...InventorySurveyMethodForm_edit
      }
      organization {
        id
        surveyMethods {
          id
          name
        }
        features {
          isEnabledSurveysVirtualWalkthroughSupport: isEnabled(feature: "SURVEYS_VIRTUAL_WALKTHROUGH_SUPPORT")
        }
      }
    }
  }
`;

export default ProjectDetailsSurveyBlock;
